import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  List,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type React from 'react';
import type { HelpTicket as Ticket } from '../../services/helpService';
import { PERMISSIONS } from '../../shared/constants/permissions';
import { formatTimestamp, getPriorityStyle, getStatusStyle } from './utils';

interface TicketListProps {
  tickets: Ticket[];
  loading: boolean;
  readTickets: string[];
  hasPermission: (permission: string) => boolean;
  onTicketClick: (ticketId: string) => void;
}

export const TicketList: React.FC<TicketListProps> = ({
  tickets,
  loading,
  readTickets,
  hasPermission,
  onTicketClick,
}) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (tickets.length === 0) {
    return (
      <Alert
        severity="info"
        sx={{
          fontSize: '.9rem',
          fontWeight: 500,
          backgroundColor: alpha(theme.palette.bidlogiq.orange, 0.5),
          color: theme.palette.text.primary,
          display: 'flex',
          alignItems: 'center',
          '& .MuiAlert-icon': {
            color: theme.palette.text.primary,
            marginRight: theme.spacing(1),
          },
        }}
      >
        No tickets found. Create a new support ticket using the "Submit Ticket" tab.
      </Alert>
    );
  }

  return (
    <List>
      {tickets.map((ticket: Ticket) => {
        const isUnread =
          ticket.responses &&
          ticket.responses.length > 0 &&
          ticket.responses[ticket.responses.length - 1]?.authorType === 'support' &&
          !readTickets.includes(ticket.id);

        return (
          <Box key={ticket.id} sx={{ mb: 2 }}>
            <Card
              onClick={() => onTicketClick(ticket.id)}
              sx={{
                cursor: 'pointer',
                boxShadow: hasPermission(PERMISSIONS.TICKET_MANAGE) ? 1 : 'none',
                backgroundColor: hasPermission(PERMISSIONS.TICKET_MANAGE)
                  ? 'inherit'
                  : 'transparent',
                border: isUnread ? `2px solid ${theme.palette.bidlogiq.orange}` : 'none',
                '&:hover': {
                  boxShadow: hasPermission(PERMISSIONS.TICKET_MANAGE) ? 4 : 'none',
                },
              }}
              data-testid={`ticket-card-${ticket.ticketNumber}`}
            >
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="h6" component="div">
                      REF: #{ticket.ticketNumber} [{ticket.category.toUpperCase()}] - {ticket.title}
                    </Typography>
                    <Typography variant="body1">
                      Created: {formatTimestamp(ticket.createdAt)}
                    </Typography>
                    <Typography variant="body1">
                      Last updated: {formatTimestamp(ticket.updatedAt)}
                    </Typography>
                    <Typography variant="body1">
                      Responses: {ticket.responses?.length || 0}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: { xs: 'flex-start', sm: 'flex-end' },
                        gap: 1,
                      }}
                    >
                      <Chip
                        label={
                          ticket.lastResponseAuthorType === 'support'
                            ? 'Awaiting User Response'
                            : 'Awaiting Support Response'
                        }
                        size="small"
                        sx={
                          (ticket.lastResponseAuthorType === 'support' &&
                            !hasPermission(PERMISSIONS.TICKET_MANAGE)) ||
                          (ticket.lastResponseAuthorType === 'user' &&
                            hasPermission(PERMISSIONS.TICKET_MANAGE))
                            ? {
                                backgroundColor: theme.palette.bidlogiq.rust,
                                color: theme.palette.common.white,
                                fontSize: '0.6rem',
                                fontWeight: 'bold',
                              }
                            : {
                                backgroundColor: theme.palette.greyLight,
                                color: theme.palette.text.primary,
                                fontSize: '0.6rem',
                                fontWeight: 'bold',
                              }
                        }
                      />
                      <Chip
                        label={ticket.status}
                        size="small"
                        sx={getStatusStyle(ticket.status, theme)}
                      />
                      <Chip
                        label={ticket.priority}
                        size="small"
                        sx={getPriorityStyle(ticket.priority, theme)}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        );
      })}
    </List>
  );
};
